import React, { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useQuery } from 'react-query'

import { Box, Button, Grid, Page, Paper, Table, Typography } from 'components'
import PieChart from 'components/Dashboard/PieChart'

import { DateTime } from 'luxon'
import { Column } from 'material-table'
import * as yup from 'yup'

import owners from 'commons/constants/complainceOwners'
import { slaSummary } from 'services/jarvisApi/complaint'
import yupValidationResolver from 'utils/yupValidationResolver'

import { Form, Select, InputNumber } from '../../components/HookForm'

// Define interfaces for the transformed data
interface IComplexityData {
  complexity: string
  total: number
  total_service_time: number
  sla: number
}

interface IMonthlyData {
  month: number
  total: number
  total_service_time: number
  sla: number
}

// Novo tipo para a tabela "SLA por Mês" (sem complexidade)
interface IByMonthOnlyRow {
  month: number
  total: number
  total_service_time: number
  sla: number
}

interface IComplexityWithMonthlyData extends IComplexityData {
  monthly: IMonthlyData[]
}

interface IYearData {
  year: number
  complexities: IComplexityWithMonthlyData[]
  total_opened: number
  total_in_analysis: number
}

// Yup validation schema for the filter form (start_year, end_year, owner)
const currentYearForValidation = DateTime.now().year
const validationSchema = yup.object().shape({
  start_year: yup
    .string()
    .transform((v) => (v === '' || v == null ? undefined : String(v)))
    .notRequired()
    .test('format-yyyy', 'Informe um ano válido (YYYY)', (val) => {
      if (!val) return true
      return /^\d{4}$/.test(String(val))
    })
    .test('max-current', 'O ano inicial não pode ser maior que o ano atual', (val) => {
      if (!val) return true
      const n = Number(val)
      return !Number.isNaN(n) && n <= currentYearForValidation
    })
    .test('start-before-or-equal-end', 'O ano inicial não pode ser maior que o ano final', function (val) {
      const end = this.parent?.end_year
      if (!val || !end) return true
      const sNum = Number(val)
      const eNum = Number(end)
      if (Number.isNaN(sNum) || Number.isNaN(eNum)) return true
      return sNum <= eNum
    }),
  end_year: yup
    .string()
    .transform((v) => (v === '' || v == null ? undefined : String(v)))
    .notRequired()
    .matches(/^\d{4}$/, 'Informe um ano válido (YYYY)')
    .test('max-current', 'O ano final não pode ser maior que o ano atual', (val) => {
      if (!val) return true
      const n = Number(val)
      return !Number.isNaN(n) && n <= currentYearForValidation
    })
    .test('end-after-or-equal-start', 'O ano final não pode ser menor que o ano inicial', function (val) {
      const start = this.parent?.start_year
      if (!val || !start) return true
      const sNum = Number(start)
      const eNum = Number(val)
      if (Number.isNaN(sNum) || Number.isNaN(eNum)) return true
      return eNum >= sNum
    }),
  owner: yup.string().notRequired().nullable()
})

const DashboardSla: React.FC = () => {
  const currentYear = String(DateTime.now().year)
  // Form/UI states as strings (Select only supports strings)
  const [startYear, setStartYear] = useState<string>(currentYear)
  const [endYear, setEndYear] = useState<string>(currentYear)
  const [owner, setOwner] = useState<string | null>(null)
  // Numeric states used for querying the API; updated only on submit
  const [startYearNum, setStartYearNum] = useState<number>(Number(currentYear))
  const [endYearNum, setEndYearNum] = useState<number>(Number(currentYear))

  const { data } = useQuery<IYearData[]>(['complaint_sla_summary', startYearNum, endYearNum, owner],
    () => slaSummary(startYearNum, endYearNum, owner || undefined).then(({ data }) => {
      // Transform the data for the table
      const yearMap = new Map<number, IYearData>()

      // Process by_year_complexity data
      if (data.by_year_complexity && Array.isArray(data.by_year_complexity)) {
        data.by_year_complexity.forEach(yearData => {
          const year = yearData.year
          const complexity = yearData.complexity
          const total_completed = parseInt(yearData.total_completed, 10)
          const total_service_time = yearData.total_service_time
          const sla = yearData.sla

          if (!yearMap.has(year)) {
            yearMap.set(year, {
              year,
              complexities: [],
              total_opened: 0,
              total_in_analysis: 0
            })
          }

          const yearEntry = yearMap.get(year)!

          // Find monthly data for this year and complexity
          const monthlyData = data.by_month_complexity
            ? data.by_month_complexity
              .filter(month => month.year === year && month.complexity === complexity)
              .map(month => ({
                month: month.month,
                total: parseInt(month.total_completed, 10),
                total_service_time: month.total_service_time,
                sla: parseFloat(month.sla)
              }))
            : []

          yearEntry.complexities.push({
            complexity,
            total: total_completed,
            total_service_time,
            sla,
            monthly: monthlyData
          })
        })
      }

      // Process complaints_opened_by_year data
      if (data.totals && data.totals.complaints_opened_by_year && Array.isArray(data.totals.complaints_opened_by_year)) {
        data.totals.complaints_opened_by_year.forEach(openedData => {
          const year = Number(openedData.year)
          const total_opened = parseInt(openedData.total_opened, 10)

          if (yearMap.has(year)) {
            const yearEntry = yearMap.get(year)!
            yearEntry.total_opened = total_opened
          } else {
            // If the year doesn't exist in yearMap, create it
            yearMap.set(year, {
              year,
              complexities: [],
              total_opened,
              total_in_analysis: 0
            })
          }
        })
      }

      // Process complaints_in_analysis_by_year data
      if (data.totals && data.totals.complaints_in_analysis_by_year && Array.isArray(data.totals.complaints_in_analysis_by_year)) {
        data.totals.complaints_in_analysis_by_year.forEach(inAnalysisData => {
          const year = Number(inAnalysisData.year)
          const total_in_analysis = parseInt(inAnalysisData.total_in_analysis, 10)

          if (yearMap.has(year)) {
            const yearEntry = yearMap.get(year)!
            yearEntry.total_in_analysis = total_in_analysis
          } else {
            // If the year doesn't exist in yearMap, create it
            yearMap.set(year, {
              year,
              complexities: [],
              total_opened: 0,
              total_in_analysis
            })
          }
        })
      }

      // Convert map to array
      return Array.from(yearMap.values())
    }),
    { refetchOnWindowFocus: false, retry: false }
  )

  // Busca separada apenas para "SLA por mês" (sem complexidade)
  const { data: byMonthOnly } = useQuery<Record<number, IByMonthOnlyRow[]>>(
    ['complaint_sla_summary_by_month_only', startYearNum, endYearNum, owner],
    () =>
      slaSummary(startYearNum, endYearNum, owner || undefined).then(({ data }) => {
        // data.by_month esperado no novo endpoint/payload
        const result: Record<number, IByMonthOnlyRow[]> = {}
        if (data.by_month && Array.isArray(data.by_month)) {
          data.by_month.forEach((m: any) => {
            const year = m.year
            const row: IByMonthOnlyRow = {
              month: m.month,
              total: parseInt(m.total_completed, 10) || 0,
              total_service_time: Number(m.total_service_time) || 0,
              sla: Number(m.sla) || 0
            }
            if (!result[year]) result[year] = []
            result[year].push(row)
          })
        }
        return result
      }),
    { refetchOnWindowFocus: false, retry: false }
  )

  const methods = useForm({ resolver: yupValidationResolver(validationSchema) })

  const onSubmit = async (formValues: Record<string, any>) => {
    // Keep UI state as strings for Selects
    const startYearStr = String(formValues.start_year ?? startYear)
    const endYearStr = String(formValues.end_year ?? endYear)
    setStartYear(startYearStr)
    setEndYear(endYearStr)

    // Cast to numbers only on submit for API
    const s = Number(startYearStr)
    const e = Number(endYearStr)

    if (!Number.isNaN(s) && !Number.isNaN(e)) {
      // Ensure start <= end; if not, swap
      const [sNorm, eNorm] = s <= e ? [s, e] : [e, s]
      setStartYearNum(sNorm)
      setEndYearNum(eNorm)
    }

    setOwner(formValues.owner || null)
  }

  useEffect(() => {
    methods.reset({ start_year: startYear, end_year: endYear, owner })
  }, [])

  // Define columns for the complexity tables
  const getComplexityColumns = (): Column<IComplexityWithMonthlyData>[] => [
    {
      title: 'Complexidade',
      field: 'complexity',
      render: (rowData) => {
        if (rowData.complexity === 'TOTAL') {
          return 'TOTAL'
        }
        const complexityMap: Record<string, string> = {
          LOW: 'Baixa',
          HIGH: 'Alta'
        }
        return complexityMap[rowData.complexity] || rowData.complexity
      }
    },
    {
      title: 'Concluídas',
      field: 'total',
      type: 'numeric'
    },
    {
      title: 'Tempo Total de Atendimento',
      field: 'total_service_time',
      type: 'numeric'
    },
    {
      title: 'SLA',
      field: 'sla',
      type: 'numeric'
    }
  ]

  // Define columns for the monthly tables
  const getMonthlyColumns = (): Column<IMonthlyData>[] => [
    {
      title: 'Mês',
      field: 'month',
      render: (rowData) => {
        if (rowData.month === 0) {
          return 'TOTAL'
        }
        const monthNames = [
          'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
          'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
        ]
        return monthNames[rowData.month - 1] || rowData.month
      }
    },
    {
      title: 'Concluídas',
      field: 'total',
      type: 'numeric'
    },
    {
      title: 'Tempo Total de Atendimento',
      field: 'total_service_time',
      type: 'numeric'
    },
    {
      title: 'SLA',
      field: 'sla',
      type: 'numeric'
    }
  ]

  // Colunas reutilizadas para a tabela "SLA por Mês"
  const getByMonthOnlyColumns = (): Column<IByMonthOnlyRow>[] => [
    {
      title: 'Mês',
      field: 'month',
      render: (rowData) => {
        if (rowData.month === 0) {
          return 'TOTAL'
        }
        const monthNames = [
          'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
          'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
        ]
        return monthNames[rowData.month - 1] || rowData.month
      }
    },
    { title: 'Concluídas', field: 'total', type: 'numeric' },
    { title: 'Tempo Total de Atendimento', field: 'total_service_time', type: 'numeric' },
    { title: 'SLA', field: 'sla', type: 'numeric' }
  ]

  // Filter out complexities that don't exist (only keep LOW and HIGH)
  const filterValidComplexities = (yearData: IYearData): IYearData => {
    return {
      ...yearData,
      complexities: yearData.complexities.filter(c => c.complexity === 'LOW' || c.complexity === 'HIGH')
    }
  }

  // Transform data for the status pie chart (opened vs in analysis)
  const getStatusPieChartData = (yearData: IYearData) => {
    return [
      { name: 'Denúncias Abertas', value: yearData.total_opened },
      { name: 'Denúncias em Análise', value: yearData.total_in_analysis }
    ]
  }

  // Transform data for the complexity pie chart (low vs high)
  const getComplexityPieChartData = (yearData: IYearData) => {
    const filteredYearData = filterValidComplexities(yearData)
    return filteredYearData.complexities.map(complexity => {
      const complexityMap: Record<string, string> = {
        LOW: 'Baixa Complexidade',
        HIGH: 'Alta Complexidade'
      }
      return {
        name: complexityMap[complexity.complexity] || complexity.complexity,
        value: complexity.total
      }
    })
  }

  // Render a table for each year
  const renderYearTables = () => {
    if (!data || data.length === 0) {
      return <Typography>Nenhum dado encontrado</Typography>
    }

    return data.map(yearData => {
      const filteredYearData = filterValidComplexities(yearData)

      // Calculate totals for each column
      const totalComplexities = [...filteredYearData.complexities]
      const totalRow = {
        complexity: 'TOTAL',
        total: totalComplexities.reduce((sum, item) => sum + item.total, 0),
        total_service_time: totalComplexities.reduce((sum, item) => sum + item.total_service_time, 0),
        sla: totalComplexities.length > 0
          ? Math.round(totalComplexities.reduce((sum, item) => sum + item.sla, 0) / totalComplexities.length)
          : 0,
        monthly: []
      }

      // Add total row to the data
      const tableData = [...totalComplexities, totalRow]

      return (
        <Grid item xs={12} md={6} key={yearData.year}>
          <Paper style={{ padding: '1rem', marginBottom: '1rem' }}>
            <Typography variant="h6">
              Resumo do Ano {yearData.year}
            </Typography>
            <Table
              data={tableData}
              columns={getComplexityColumns()}
              options={{
                paging: false,
                search: false,
                sorting: false,
                headerStyle: {
                  fontWeight: 'bold'
                },
                rowStyle: (rowData) => ({
                  backgroundColor: rowData.complexity === 'TOTAL' ? '#f5f5f5' : 'inherit',
                  fontWeight: rowData.complexity === 'TOTAL' ? 'bold' : 'normal'
                })
              }}
            />
            <Box mt={2}>
              <Typography variant="subtitle1" style={{ fontWeight: 'bold' }}>
                Totalizadores:
              </Typography>
              <Typography>
                Total de Denúncias Abertas: {filteredYearData.total_opened}
              </Typography>
              <Typography>
                Total de Denúncias em Análise: {filteredYearData.total_in_analysis}
              </Typography>
            </Box>
          </Paper>
        </Grid>
      )
    })
  }

  // Render monthly tables for each year and complexity
  const renderMonthlyTables = () => {
    if (!data || data.length === 0) {
      return null
    }

    return data.map(yearData => {
      const filteredYearData = filterValidComplexities(yearData)

      return filteredYearData.complexities.map(complexityData => {
        // Skip if there's no monthly data
        if (!complexityData.monthly || complexityData.monthly.length === 0) {
          return null
        }

        const complexityMap: Record<string, string> = {
          LOW: 'Baixa',
          HIGH: 'Alta'
        }

        const complexityName = complexityMap[complexityData.complexity] || complexityData.complexity

        // Calculate totals for each column
        const monthlyData = [...complexityData.monthly]
        const totalRow = {
          month: 0, // Use 0 to represent "TOTAL"
          total: monthlyData.reduce((sum, item) => sum + item.total, 0),
          total_service_time: monthlyData.reduce((sum, item) => sum + item.total_service_time, 0),
          sla: monthlyData.length > 0
            ? Math.round(monthlyData.reduce((sum, item) => sum + item.sla, 0) / monthlyData.length)
            : 0
        }

        // Add total row to the data
        const tableData = [...monthlyData, totalRow]

        return (
          <Grid item xs={12} md={6} key={`${yearData.year}-${complexityData.complexity}`}>
            <Paper style={{ padding: '1rem', marginBottom: '1rem' }}>
              <Typography variant="h6">
                Dados Mensais do Ano {yearData.year} - Complexidade {complexityName}
              </Typography>
              <Table
                data={tableData}
                columns={getMonthlyColumns()}
                options={{
                  paging: false,
                  search: false,
                  sorting: false,
                  headerStyle: {
                    fontWeight: 'bold'
                  },
                  rowStyle: (rowData) => ({
                    backgroundColor: rowData.month === 0 ? '#f5f5f5' : 'inherit',
                    fontWeight: rowData.month === 0 ? 'bold' : 'normal'
                  })
                }}
              />
            </Paper>
          </Grid>
        )
      })
    })
  }

  // Renderiza as tabelas de SLA por mês (sem complexidade)
  const renderByMonthOnlyTables = () => {
    if (!byMonthOnly || Object.keys(byMonthOnly).length === 0) return null

    return Object.entries(byMonthOnly).map(([yearStr, rows]) => {
      const year = Number(yearStr)
      if (!rows || rows.length === 0) return null

      const totalRow: IByMonthOnlyRow = {
        month: 0,
        total: rows.reduce((s, r) => s + (r.total || 0), 0),
        total_service_time: rows.reduce((s, r) => s + (r.total_service_time || 0), 0),
        sla: rows.length > 0 ? Math.round(rows.reduce((s, r) => s + (r.sla || 0), 0) / rows.length) : 0
      }
      const tableData = [...rows, totalRow]

      return (
        <Grid item xs={12} md={6} key={`by-month-only-${year}`}>
          <Paper style={{ padding: '1rem', marginBottom: '1rem' }}>
            <Typography variant="h6">SLA por Mês - {year}</Typography>
            <Table
              data={tableData}
              columns={getByMonthOnlyColumns()}
              options={{
                paging: false,
                search: false,
                sorting: false,
                headerStyle: { fontWeight: 'bold' },
                rowStyle: (rowData) => ({
                  backgroundColor: rowData.month === 0 ? '#f5f5f5' : 'inherit',
                  fontWeight: rowData.month === 0 ? 'bold' : 'normal'
                })
              }}
            />
          </Paper>
        </Grid>
      )
    })
  }

  return (
    <Page title="Dashboard SLA">
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Box display="flex" justifyContent="center" alignItems="center">
            <Box flexWrap="nowrap">
              <Paper style={{ padding: '1rem' }}>
                <Form onSubmit={onSubmit} methods={methods}>
                  <Grid
                    container
                    spacing={2}
                    justifyContent="center"
                    alignItems="center"
                  >
                    <Grid item xs={12} sm={6}>
                      <InputNumber
                        name="start_year"
                        label="Ano inicial"
                        placeholder="YYYY"
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <InputNumber
                        name="end_year"
                        label="Ano final"
                        placeholder="YYYY"
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} sm={12}>
                      <Select
                        options={Object.values(owners)}
                        name="owner"
                        label="Owner (opcional)"
                      />
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Button color="primary" fullWidth type="submit">
                        Filtrar
                      </Button>
                    </Grid>
                  </Grid>
                </Form>
              </Paper>
            </Box>
          </Box>
        </Grid>

        {data && data.length > 0 && (
          <>
            <Grid item xs={12}>
              <Typography
                variant="h5"
                style={{ marginTop: '2rem', marginBottom: '1rem' }}
              >
                Gráficos de Denúncias
              </Typography>
            </Grid>

            {data.map((yearData) => {
              const filteredYearData = filterValidComplexities(yearData)
              return (
                <React.Fragment key={`charts-${yearData.year}`}>
                  <Grid item xs={12} md={6}>
                    <PieChart
                      label={`Denúncias por Status - ${yearData.year}`}
                      data={getStatusPieChartData(yearData)}
                      height={400}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <PieChart
                      label={`Denúncias por Complexidade - ${yearData.year}`}
                      data={getComplexityPieChartData(filteredYearData)}
                      height={400}
                    />
                  </Grid>
                </React.Fragment>
              )
            })}
          </>
        )}

        <Grid item xs={12}>
          <Typography
            variant="h5"
            style={{ marginTop: '2rem', marginBottom: '1rem' }}
          >
            Dados por Ano e Complexidade
          </Typography>
        </Grid>

        {renderYearTables()}

        <Grid item xs={12}>
          <Typography
            variant="h5"
            style={{ marginTop: '2rem', marginBottom: '1rem' }}
          >
            Dados Mensais por Ano e Complexidade
          </Typography>
        </Grid>

        {renderMonthlyTables()}

        <Grid item xs={12}>
          <Typography
            variant="h5"
            style={{ marginTop: '2rem', marginBottom: '1rem' }}
          >
            SLA por Mês
          </Typography>
        </Grid>

        {renderByMonthOnlyTables()}
      </Grid>
    </Page>
  )
}

export default DashboardSla
